/**
 * Toss Payments JS SDK wrapper.
 *
 * This module lazy-loads the @tosspayments/payment-sdk via dynamic
 * import so it is code-split by Vite and only fetched when a user
 * actually hits a Toss checkout flow. All SDK access is contained
 * here — page components import `requestTossCardPayment` and never
 * touch the SDK directly.
 *
 * See .claude/specs/008-web-toss-sdk-integration/subSPEC-01-toss-sdk-wrapper.md.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Minimal shape of the TossPayments instance returned by
 * loadTossPayments(clientKey). Declared locally so this module does
 * not leak the full SDK type surface into consumers.
 */
interface TossPaymentsInstance {
  requestPayment(
    method: 'CARD',
    options: TossRequestPaymentOptions,
  ): Promise<void>;
}

interface TossRequestPaymentOptions {
  amount: number;
  orderId: string;
  orderName: string;
  successUrl: string;
  failUrl: string;
  customerName?: string;
  customerEmail?: string;
}

export interface RequestTossCardPaymentInput {
  readonly sessionId: string;
  readonly checkoutToken: string;
  /** MUST equal sessionId — server invariant */
  readonly orderId: string;
  readonly orderName: string;
  /** KRW whole-won positive integer */
  readonly amount: number;
  readonly customerName?: string;
  readonly customerEmail?: string;
}

export class TossSdkError extends Error {
  readonly code:
    | 'toss_sdk_load_failed'
    | 'toss_client_key_missing'
    | 'toss_request_payment_failed';

  constructor(
    code: TossSdkError['code'],
    message: string,
    readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'TossSdkError';
    this.code = code;
  }
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TOSS_METHOD_CARD = 'CARD' as const;

// ---------------------------------------------------------------------------
// Memoized loader
// ---------------------------------------------------------------------------

let _tossLoader: Promise<TossPaymentsInstance> | null = null;

/**
 * Lazy-load and memoize the TossPayments instance. Subsequent calls
 * return the same underlying promise. Load failure clears the memo
 * so a retry is possible on a flaky network.
 *
 * Throws TossSdkError('toss_client_key_missing') when the env var
 * is empty. Throws TossSdkError('toss_sdk_load_failed') when the
 * dynamic import fails.
 */
export function loadToss(): Promise<TossPaymentsInstance> {
  if (_tossLoader !== null) return _tossLoader;

  const clientKey = (
    import.meta.env.VITE_TOSS_CLIENT_KEY ?? ''
  ).trim();

  if (!clientKey) {
    throw new TossSdkError(
      'toss_client_key_missing',
      'VITE_TOSS_CLIENT_KEY is not set at build time',
    );
  }

  _tossLoader = (async () => {
    try {
      const mod = await import('@tosspayments/payment-sdk');
      const tp = await mod.loadTossPayments(clientKey);
      return tp as unknown as TossPaymentsInstance;
    } catch (err: unknown) {
      // Clear memo so a subsequent call can retry instead of
      // permanently remembering a failed load.
      _tossLoader = null;
      throw new TossSdkError(
        'toss_sdk_load_failed',
        err instanceof Error ? err.message : 'Unknown SDK load failure',
        err,
      );
    }
  })();

  return _tossLoader;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Request a card payment through the Toss SDK. On success, Toss
 * redirects the browser to /toss/success — this function never
 * returns normally in the success path.
 *
 * successUrl / failUrl are constructed internally as:
 *   ${origin}/toss/success?token=<jwt>&sessionId=<id>
 *   ${origin}/toss/fail?token=<jwt>&sessionId=<id>
 *
 * Callers MUST NOT supply their own success/fail URLs.
 */
export async function requestTossCardPayment(
  input: RequestTossCardPaymentInput,
): Promise<void> {
  if (input.orderId !== input.sessionId) {
    throw new TossSdkError(
      'toss_request_payment_failed',
      'orderId must equal sessionId',
    );
  }
  if (!Number.isInteger(input.amount) || input.amount < 1) {
    throw new TossSdkError(
      'toss_request_payment_failed',
      'amount must be a positive integer',
    );
  }

  const tp = await loadToss();

  const base = window.location.origin;
  const returnQuery = new URLSearchParams({
    token: input.checkoutToken,
    sessionId: input.sessionId,
  }).toString();
  const successUrl = `${base}/toss/success?${returnQuery}`;
  const failUrl = `${base}/toss/fail?${returnQuery}`;

  const options: TossRequestPaymentOptions = {
    amount: input.amount,
    orderId: input.orderId,
    orderName: input.orderName,
    successUrl,
    failUrl,
  };
  if (input.customerName) {
    options.customerName = input.customerName;
  }
  if (input.customerEmail) {
    options.customerEmail = input.customerEmail;
  }

  try {
    await tp.requestPayment(TOSS_METHOD_CARD, options);
  } catch (err: unknown) {
    throw new TossSdkError(
      'toss_request_payment_failed',
      err instanceof Error ? err.message : 'Toss requestPayment failed',
      err,
    );
  }
}
